import { defineStore } from "pinia";
import { Curriculum } from "@/models/Curriculum.model";
import { getCurriculumRegistry } from "@/config/curricula.registry";
import { useUsersStore } from "@/stores/Users.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import config from "@/config/app.config";

/**
 * Plain Pinia store (not AbstractBaseStore — no REST resource behind it).
 * The registry is a static JSON import for now; when it becomes an API
 * endpoint, swap the implementation in curricula.registry.ts.
 */
export const useCurriculaStore = defineStore("curricula", {
	getters: {
		all(): Curriculum[] {
			// Key-built curricula serve the SHOW_ALL_CURRICULA testing aid only. A bare key
			// resolves no static goal-source file (production organisations carry real ids
			// like "GO_2023"), so GO/SLO list no goals in that mode — accepted trade-off.
			return getCurriculumRegistry().map((entry) => new Curriculum(entry.key));
		},

		byKey() {
			// Unknown keys degrade to a fallback instance inside the Curriculum constructor.
			return (key: string): Curriculum => new Curriculum(key);
		},

		/**
		 * Deduped union of the current user's active organisations' curricula.
		 * Empty when the user has no organisations with a curriculum set.
		 *
		 * With config.SHOW_ALL_CURRICULA on, the organisation filter is skipped entirely and
		 * the full registry is offered instead — a testing aid for reaching a curriculum you
		 * have no (active) school for. Registry curricula are built from their keys, so an
		 * organisation-specific id (e.g. a curriculum not in the registry) is not listed.
		 */
		availableForUser(): Curriculum[] {
			if (config.SHOW_ALL_CURRICULA) {
				return this.all;
			}

			const me = useUsersStore().me;
			if (!me) {
				return [];
			}

			const seen = new Set<string>();
			const result: Curriculum[] = [];

			for (const role of me.roles) {
				if (!role.active || !role.organisation?.active) {
					continue;
				}

				const curriculumId = role.organisation.curriculumId;
				if (!curriculumId) {
					continue;
				}

				const curriculum = new Curriculum(curriculumId);

				// A curriculum the registry does not know has no goal source, no picker and no
				// viewer behind it, so offering it only produces an empty goal selector. Report
				// the misconfigured school instead of listing it.
				if (!curriculum.key) {
					console.error(
						`Organisation ${role.organisation.id} ("${role.organisation.name}") is configured with ` +
						`curriculum "${curriculumId}", which is not in curricula.json — not offering it as a goal source.`
					);
					continue;
				}

				if (seen.has(curriculum.key)) {
					continue;
				}

				seen.add(curriculum.key);
				result.push(curriculum);
			}

			return result;
		},

		/**
		 * The current school's curriculum as a one-element array — the school-scope
		 * counterpart of availableForUser (agenda, theme weeks: only the school the
		 * user is working in). Empty when there is no current organisation, it has no
		 * curriculum, or the id is unknown to the registry (logged — a school
		 * configured with an unknown id is misconfigured; nothing resolves behind it).
		 *
		 * config.SHOW_ALL_CURRICULA overrides this scope too (testing aid).
		 */
		availableForCurrentSchool(): Curriculum[] {
			if (config.SHOW_ALL_CURRICULA) {
				return this.all;
			}

			const organisation = useOrganisationsStore().currentOrganisation;
			const curriculumId = organisation?.curriculumId;
			if (!curriculumId) {
				return [];
			}

			const curriculum = new Curriculum(curriculumId);
			if (!curriculum.key) {
				console.error(
					`Organisation ${organisation.id} ("${organisation.name}") is configured with ` +
					`curriculum "${curriculumId}", which is not in curricula.json — not offering it as a goal source.`
				);
				return [];
			}

			return [curriculum];
		},
	},
});
